'use client';

import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import Link from 'next/link';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';

import {
	Button,
	Dialog,
	DotsThreeVerticalIcon,
	ProtectedRoleComponent,
} from '@/shared';
import { urlBuilder } from '@/shared/utils';

import {
	getAdminAiProblemAction,
	type GetAdminAiProblemActionResult,
} from '../actions/get-admin-ai-problem-action';

type AdminAiProblemJsonButtonProps = {
	problemId: string;
};

export function AdminAiProblemJsonButton({
	problemId,
}: AdminAiProblemJsonButtonProps) {
	const [open, setOpen] = useState(false);
	const [loading, setLoading] = useState(false);
	const [result, setResult] = useState<GetAdminAiProblemActionResult | null>(
		null,
	);
	const latestProblemIdRef = useRef(problemId);

	const json = useMemo(() => {
		if (!result?.data) return '';
		return JSON.stringify(result.data, null, 2);
	}, [result]);

	useEffect(() => {
		latestProblemIdRef.current = problemId;
		setOpen(false);
		setLoading(false);
		setResult(null);
	}, [problemId]);

	const loadProblem = async () => {
		if (result || loading) return;
		const requestedProblemId = problemId;
		setLoading(true);
		try {
			const nextResult = await getAdminAiProblemAction(requestedProblemId);
			if (latestProblemIdRef.current === requestedProblemId) {
				setResult(nextResult);
			}
		} catch {
			if (latestProblemIdRef.current === requestedProblemId) {
				setResult({
					error: 'Не удалось загрузить административные данные задачи.',
				});
			}
		} finally {
			if (latestProblemIdRef.current === requestedProblemId) {
				setLoading(false);
			}
		}
	};

	const handleOpen = () => {
		setOpen(true);
		void loadProblem();
	};

	return (
		<ProtectedRoleComponent allowedRoles={['admin', 'editor']}>
			<div className="flex items-center justify-end gap-3 rounded-full bg-gray-5 p-1">
				<Menu>
					<MenuButton as={Fragment}>
						{({ active }) => (
							<Button
								type="button"
								size="sm"
								variant="plain-gray"
								disabled={active}
								aria-label="Открыть меню задачи"
							>
								<DotsThreeVerticalIcon className="size-10 rounded-full bg-white p-2" />
							</Button>
						)}
					</MenuButton>
					<MenuItems
						anchor="bottom end"
						transition
						className="z-50 rounded-lg bg-white p-2 shadow-listbox ring-1 ring-inset ring-black/5 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
					>
						<MenuItem as={Fragment}>
							<button
								type="button"
								onClick={handleOpen}
								className="block w-full rounded-lg px-3 py-2 text-left text-sm data-[focus]:bg-gray-5 data-[selected]:bg-gray-5"
							>
								Просмотр JSON
							</button>
						</MenuItem>
						<MenuItem as={Fragment}>
							<Link
								href={urlBuilder.aiProblemEdit(problemId)}
								className="block w-full rounded-lg px-3 py-2 text-left text-sm data-[focus]:bg-gray-5 data-[selected]:bg-gray-5"
							>
								Редактировать
							</Link>
						</MenuItem>
					</MenuItems>
				</Menu>
			</div>

			<Dialog
				open={open}
				onClose={setOpen}
				title="JSON задачи"
				className="md:max-w-[860px] xl:max-w-[1200px]"
				footer={
					<div className="flex items-center justify-end gap-4 border-t border-gray-4 p-4">
						<Button variant="outline" onClick={() => setOpen(false)}>
							Закрыть
						</Button>
					</div>
				}
			>
				<div className="p-4">
					{loading && (
						<div className="py-10 text-center text-sm text-gray-3">
							Загружаем JSON...
						</div>
					)}

					{!loading && result?.error && (
						<div className="rounded-lg border border-red bg-red-light p-4 text-sm text-red-hover">
							{result.error}
						</div>
					)}

					{!loading && json && (
						<pre className="max-h-[70vh] overflow-auto whitespace-pre-wrap rounded-lg bg-gray-5 p-4 font-mono text-xs leading-5 text-black">
							{json}
						</pre>
					)}
				</div>
			</Dialog>
		</ProtectedRoleComponent>
	);
}
